import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Source } from '../services/api';
import './ContractPicker.css';

export interface SourcePickerProps {
  sources: Source[];
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  allowEmpty?: boolean;
  allowCreate?: boolean;
  onCreate?: (name: string) => void | Promise<void>;
  className?: string;
  ariaLabel?: string;
}

type PopupPosition = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

const POPUP_MAX_HEIGHT = 320;

export const SourcePicker: React.FC<SourcePickerProps> = ({
  sources,
  value,
  onChange,
  disabled,
  placeholder = 'Поиск…',
  emptyLabel = '—',
  allowEmpty = true,
  allowCreate = false,
  onCreate,
  className,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sortedSources = useMemo(
    () => [...sources].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [sources],
  );

  const selectedLabel = useMemo(() => {
    if (value == null) return '';
    return sortedSources.find((source) => source.id === value)?.name ?? '';
  }, [sortedSources, value]);

  const filteredSources = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedSources;
    return sortedSources.filter((source) => {
      const haystack = [source.name, source.description].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedSources, query]);

  const canCreateFromQuery = useMemo(() => {
    if (!allowCreate || !onCreate) return false;
    const q = query.trim();
    if (!q) return false;
    return !sortedSources.some((source) => source.name.trim().toLowerCase() === q.toLowerCase());
  }, [allowCreate, onCreate, query, sortedSources]);

  const updatePopupPosition = useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(POPUP_MAX_HEIGHT, Math.max(120, openUp ? spaceAbove : spaceBelow));

    setPopupPosition({
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePopupPosition();
    const onScrollOrResize = (): void => updatePopupPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePopupPosition]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
      setPopupPosition(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setPopupPosition(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const closePicker = (): void => {
    setOpen(false);
    setQuery('');
    setPopupPosition(null);
  };

  const handleCreate = async (): Promise<void> => {
    if (!canCreateFromQuery || !onCreate || creating) return;
    const name = query.trim();
    setCreating(true);
    try {
      await onCreate(name);
      closePicker();
    } finally {
      setCreating(false);
    }
  };

  const openPicker = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const popup =
    open && popupPosition
      ? createPortal(
          <div
            ref={popupRef}
            className="contract-picker__popup contract-picker__popup--portal"
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: 'fixed',
              left: popupPosition.left,
              width: popupPosition.width,
              maxHeight: popupPosition.maxHeight,
              top: popupPosition.top,
              bottom: popupPosition.bottom,
            }}
          >
            {allowEmpty ? (
              <button
                type="button"
                role="option"
                aria-selected={value == null}
                className={`contract-picker__option${value == null ? ' contract-picker__option--active' : ''}`}
                onClick={() => {
                  onChange(null);
                  closePicker();
                }}
              >
                <span className="contract-picker__option-num">{emptyLabel}</span>
              </button>
            ) : null}
            {canCreateFromQuery ? (
              <button
                type="button"
                role="option"
                className="contract-picker__option"
                onClick={() => {
                  void handleCreate();
                }}
                disabled={creating}
              >
                <span className="contract-picker__option-num">
                  {creating ? 'Создание…' : `Создать «${query.trim()}»`}
                </span>
              </button>
            ) : null}
            {filteredSources.length === 0 && !canCreateFromQuery ? (
              <div className="contract-picker__empty">Ничего не найдено</div>
            ) : (
              filteredSources.map((source) => {
                const isActive = source.id === value;
                return (
                  <button
                    type="button"
                    key={source.id}
                    role="option"
                    aria-selected={isActive}
                    className={`contract-picker__option${isActive ? ' contract-picker__option--active' : ''}`}
                    onClick={() => {
                      onChange(source.id);
                      closePicker();
                    }}
                  >
                    <span className="contract-picker__option-num">{source.name}</span>
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className={`contract-picker${disabled ? ' contract-picker--disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={containerRef}
    >
      {open ? (
        <input
          ref={inputRef}
          type="search"
          className="contract-picker__field"
          placeholder={selectedLabel || placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          aria-label={ariaLabel ?? 'Поиск в списке'}
          autoComplete="off"
        />
      ) : (
        <button
          type="button"
          className="contract-picker__trigger"
          onClick={openPicker}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="contract-picker__text">
            {selectedLabel || (allowEmpty ? emptyLabel : placeholder)}
          </span>
          <span className="contract-picker__caret" aria-hidden>
            ▾
          </span>
        </button>
      )}
      {popup}
    </div>
  );
};

export default SourcePicker;
